import Decimal from 'decimal.js';

export const formatDecimalsOptions = {
  currency: 'USD',
  currencyDisplay: 'hidden',
  precision: 3,
  valueRange: {
    min: 0,
    max: 999999999,
  },
  hideCurrencySymbolOnFocus: true,
  hideGroupingSeparatorOnFocus: true,
  hideNegligibleDecimalDigitsOnFocus: true,
  autoDecimalDigits: false,
  useGrouping: true,
  accountingSign: false,
};

export const formatIntegerOptions = {
  currency: 'USD',
  currencyDisplay: 'hidden',
  precision: 0,
  valueRange: {
    min: 0,
    max: 999999999,
  },
  hideCurrencySymbolOnFocus: true,
  hideGroupingSeparatorOnFocus: true,
  hideNegligibleDecimalDigitsOnFocus: true,
  autoDecimalDigits: false,
  useGrouping: true,
  accountingSign: false,
};

export const formatCurrencyOptions = {
  ...formatDecimalsOptions,
  precision: 2,
};

export const formatNumber = (value: number | undefined, format = {}) => {
  if (!value) return '0';
  return new Intl.NumberFormat('en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    ...format,
  }).format(value);
};

export const formatCurrency = (value: number | undefined = 0, currency: string = 'USD') => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
  }).format(value);
};

/**
 * Converts Intl Formatted number string to Number
 * @param value
 */
export const convertToNumber = (value: string | number) => {
  return Number(String(value).replace(/[^\d.-]+/g, ''));
};

const WeightUnits = Object.freeze({
  KGS: 'KGS',
  LBS: 'LBS',
});

const VolumeUnits = Object.freeze({
  CBM: 'CBM',
  CFT: 'CFT',
});

interface DecimalOptions {
  decimalPlacesFrom?: number;
  decimalPlacesTo?: number;
}

const converterFactory = (constant: number, unit: string, decimalOptions: DecimalOptions = {}) => {
  const { decimalPlacesFrom = 3, decimalPlacesTo = 2 } = decimalOptions;

  const convertTo = (value: number) =>
    new Decimal(value * constant)?.toDecimalPlaces(decimalPlacesTo)?.toNumber();
  const convertFrom = (value: number) =>
    new Decimal(value / constant)?.toDecimalPlaces(decimalPlacesFrom)?.toNumber();
  const convert = (value: number, toUnit: string) =>
    unit === toUnit ? convertFrom(value) : convertTo(value);

  return {
    convert,
    convertTo,
    convertFrom,
  };
};

const KG_TO_LBS = 2.20462;
const CBM_TO_CFT = 35.3146667;

const kgToLbConverter = converterFactory(KG_TO_LBS, WeightUnits.KGS);
const cmbToCftConverter = converterFactory(CBM_TO_CFT, VolumeUnits.CFT);

export const getReceivedWeightInKgs = (item: any) => {
  const receivedWeight = item?.receivedWeight ?? 0;

  return item?.receivedWeightUnit?.toUpperCase() === WeightUnits.KGS
    ? new Decimal(receivedWeight)?.toDecimalPlaces(3)?.toNumber()
    : kgToLbConverter.convertFrom(receivedWeight);
};

export const getReceivedWeightInLbs = (item: any) => {
  const receivedWeight = item?.receivedWeight ?? 0;

  return item?.receivedWeightUnit?.toUpperCase() === WeightUnits.LBS
    ? new Decimal(receivedWeight)?.toDecimalPlaces(2)?.toNumber()
    : kgToLbConverter.convertTo(receivedWeight);
};

export const getReceivedVolumeInCbm = (item: any) => {
  const receivedVolume = item?.receivedVolume ?? 0;

  return item?.receivedVolumeUnit?.toUpperCase() === VolumeUnits.CBM
    ? new Decimal(receivedVolume)?.toDecimalPlaces(3)?.toNumber()
    : cmbToCftConverter.convertFrom(receivedVolume);
};

export const getReceivedVolumeInCft = (item: any) => {
  const receivedVolume = item?.receivedVolume ?? 0;

  return item?.receivedVolumeUnit?.toUpperCase() === VolumeUnits.CFT
    ? new Decimal(receivedVolume)?.toDecimalPlaces(2)?.toNumber()
    : cmbToCftConverter.convertTo(receivedVolume);
};
